import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Auth0Provider } from './providers/Auth0Provider';
import { Auth0AuthProvider, DevAuthProvider, useAuth } from './contexts/Auth0Context';
// Eager imports — needed on initial landing / auth flow / layout
import WelcomePage from './components/WelcomePage.jsx';
import { isNative } from './utils/platform.js';
import AboutPage from './pages/AboutPage.jsx';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.jsx';
import AudioNotificationHost from './components/audio/AudioNotificationHost.jsx';
import { getButtonClass } from './lib/tailwindComponents';
import AuthError from './components/AuthError.jsx';
import RegistrationFlow from './components/RegistrationFlow.jsx';
import IdleTimeoutWrapper from './components/IdleTimeoutWrapper.jsx';
import AdminRoute from './components/admin/AdminRoute.jsx';
import { AudioStreamProvider } from './context/audioStreamContext.jsx';
import { MusicProvider, MusicE2EGateBridge, MusicRouteGateBridge } from './contexts/MusicContext.jsx';
import { PlayerPreferencesProvider } from './contexts/PlayerPreferencesContext.jsx';
import { loggers } from './utils/logger.js';
import { consumeAuthReturnTo, getAuthReturnTo, setAuthReturnTo } from './utils/authRedirect.js';
import { apiUrl } from './config/api.js';

// Lazy imports — loaded on demand per route
const App = React.lazy(() => import('./App'));
const PlayerPage = React.lazy(() => import('./components/player/PlayerPage.jsx'));
// CombatArenaPage removed — combat arena now renders inside PlayerPage via PlayerRoomShell
const BillingSuccessPage = React.lazy(() => import('./pages/BillingSuccessPage.jsx'));
const BillingCancelPage = React.lazy(() => import('./pages/BillingCancelPage.jsx'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage.jsx'));
const CharacterCreationPage = React.lazy(() => import('./pages/CharacterCreationPage.jsx'));
const CharactersPage = React.lazy(() => import('./pages/CharactersPage.jsx'));
const FastCharacterCreator = React.lazy(() => import('./pages/FastCharacterCreator.jsx'));
const JoinCampaignPage = React.lazy(() => import('./pages/JoinCampaignPage.jsx'));
const TavernPage = React.lazy(() => import('./pages/TavernPage.jsx'));
const TavernChatPage = React.lazy(() => import('./pages/TavernChatPage.jsx'));
const AdventureModeLanding = React.lazy(() => import('./pages/AdventureModeLanding.jsx'));
const ArenaPage = React.lazy(() => import('./pages/ArenaPage.jsx'));
const TavernAppShell = React.lazy(() => import('./pages/TavernAppShell.jsx'));
const TavernAppChat = React.lazy(() => import('./pages/TavernAppChat.jsx'));
const DeleteAccountPage = React.lazy(() => import('./pages/DeleteAccountPage.jsx'));
const NewCharacterFlowPage = React.lazy(() => import('./components/onboarding/NewCharacterFlowPage.jsx'));

// Admin
const AgentSettings = React.lazy(() => import('./components/admin/AgentSettings.jsx'));
const SceneInspector = React.lazy(() => import('./components/admin/SceneInspector.jsx'));
const UserManagement = React.lazy(() => import('./components/admin/UserManagement.jsx'));
const CampaignInspector = React.lazy(() => import('./components/admin/CampaignInspector.jsx'));
const CharacterInspector = React.lazy(() => import('./components/admin/CharacterInspector.jsx'));
const AdminIndex = React.lazy(() => import('./components/admin/AdminIndex.jsx'));
const ConversationDesignerPage = React.lazy(() => import('./components/admin/conversation-designer/ConversationDesignerPage.jsx'));
const CombatPlaygroundPage = React.lazy(() => import('./components/admin/CombatPlaygroundPage.jsx'));
const WeaponMechanicsLabPage = React.lazy(() => import('./components/admin/weapon-mechanics-lab/WeaponMechanicsLabPage.jsx'));
const WeaponFlowLabPage = React.lazy(() => import('./components/admin/weapon-flow-lab/WeaponFlowLabPage.jsx'));
const CombatAnimationLabPage = React.lazy(() => import('./components/admin/CombatAnimationLabPage.jsx'));
const AnimationEditorPage = React.lazy(() => import('./components/admin/animation-editor/AnimationEditorPage.jsx'));
const CampaignStartupTester = React.lazy(() => import('./components/admin/CampaignStartupTester.jsx'));
const PreferencesInspector = React.lazy(() => import('./components/admin/PreferencesInspector.jsx'));
const StructuredTimeline = React.lazy(() => import('./components/admin/StructuredTimeline.jsx'));
const CampaignViewer = React.lazy(() => import('./components/admin/CampaignViewer.jsx'));
const NPCCreator = React.lazy(() => import('./components/admin/NPCCreator.jsx'));
const SceneFlowInspector = React.lazy(() => import('./components/admin/SceneFlowInspector.jsx'));
const SceneImageVisualization = React.lazy(() => import('./components/admin/SceneImageVisualization.jsx'));
const PregenerationAdminRouteLayout = React.lazy(() => import('./components/admin/pregeneration-pipeline/PregenerationAdminRouteLayout.jsx'));
const PregenerationPipelineRoute = React.lazy(() => import('./components/admin/pregeneration-pipeline/PregenerationPipelineRoute.jsx'));
const PregenerationWorkbenchPage = React.lazy(() => import('./components/admin/pregeneration-workbench/PregenerationWorkbenchPage.jsx'));
const ImageLabRoute = React.lazy(() => import('./components/admin/ImageLabRoute.jsx'));
const SceneFlightLab = React.lazy(() => import('./components/admin/SceneFlightLab.jsx'));
const VideoLab = React.lazy(() => import('./components/admin/VideoLab.jsx'));
const ActivityDashboard = React.lazy(() => import('./components/admin/ActivityDashboard.jsx'));
const KnowledgeLayerViewer = React.lazy(() => import('./components/admin/KnowledgeLayerViewer.jsx'));
const SoulForgeAdmin = React.lazy(() => import('./components/admin/SoulForgeAdmin.jsx'));
const VoiceManager = React.lazy(() => import('./components/admin/VoiceManager.jsx'));
const TurnIntegrityDashboard = React.lazy(() => import('./components/admin/TurnIntegrityDashboard.jsx'));
const InteractiveSceneLab = React.lazy(() => import('./components/admin/InteractiveSceneLab.jsx'));
const CharacterPlacementLab = React.lazy(() => import('./components/admin/CharacterPlacementLab.jsx'));
const ThreeLab = React.lazy(() => import('./components/admin/three-lab/ThreeLab.jsx'));
const CompanionStudioPage = React.lazy(() => import('./components/admin/companion-studio/CompanionStudioPage.jsx'));
const NarrativeArcPage = React.lazy(() => import('./components/admin/narrative-arc/NarrativeArcPage.jsx'));

function NarrativeArcRoutePage() {
  // Narrative Arc V2 is the shipped workbench. Both historical route shapes
  // intentionally resolve to it during the completed cutover.
  return <NarrativeArcPage />;
}

const CompanionFlowPage = React.lazy(() => import('./components/companion/CompanionFlowPage.jsx'));
const AudioDebugPage = React.lazy(() => import('./components/debug/AudioDebugPage.jsx').then(m => ({ default: m.AudioDebugPage })));
const ImageDebugPage = React.lazy(() => import('./components/debug/ImageDebugPage.jsx').then(m => ({ default: m.ImageDebugPage })));

// Test pages
const CollaborativeEditorTest = React.lazy(() => import('./pages/CollaborativeEditorTest.jsx'));
const SceneImagesDebugPage = React.lazy(() => import('./pages/SceneImagesDebugPage.jsx'));
const SceneCreatorTestPage = React.lazy(() => import('./pages/SceneCreatorTestPage.jsx'));
const CharacterExtractionTestPage = React.lazy(() => import('./pages/CharacterExtractionTestPage.jsx'));
const SceneTransitionTestPage = React.lazy(() => import('./pages/SceneTransitionTestPage.jsx'));
const CampaignGenerationTestPage = React.lazy(() => import('./pages/CampaignGenerationTestPage.jsx'));
const DiceRollerTest = React.lazy(() => import('./pages/DiceRollerTest.jsx'));
const CombatDisplayTestPage = React.lazy(() => import('./pages/CombatDisplayTestPage.jsx'));
const ContextTestPage = React.lazy(() => import('./pages/ContextTestPage.jsx'));
const JourneyPage = React.lazy(() => import('./pages/JourneyPage.jsx'));
const JourneyLab = React.lazy(() => import('./components/admin/JourneyLab.jsx'));
const JourneyCharacterPage = React.lazy(() => import('./pages/JourneyCharacterPage.jsx'));
const JourneyHomePage = React.lazy(() => import('./pages/JourneyHomePage.jsx'));
const JourneyLibrary = React.lazy(() => import('./components/admin/JourneyLibrary.jsx'));
const JourneyTravellers = React.lazy(() => import('./components/admin/JourneyTravellers.jsx'));
const JourneyEquipment = React.lazy(() => import('./components/admin/JourneyEquipment.jsx'));
const JourneyNpcs = React.lazy(() => import('./components/admin/JourneyNpcs.jsx'));
const JourneyPois = React.lazy(() => import('./components/admin/JourneyPois.jsx'));
const JourneyBorders = React.lazy(() => import('./components/admin/JourneyBorders.jsx'));
const JourneyTextureLab = React.lazy(() => import('./components/admin/JourneyTextureLab.jsx'));
/**
 * /journey is both the front door and the surface. Which one depends on
 * whether a run has been named: no ?journey= and no ?world= means the player
 * has not chosen anything yet, and the menu is what they want.
 */
const JourneyGate = () => {
  // useSearchParams, not window.location: the path does not change when a
  // journey is chosen, so a gate reading the raw location would render the
  // menu once and never notice the run that was just begun.
  const [params] = useSearchParams();
  return params.get('journey') || params.get('world')
    ? <JourneyPage />
    : <JourneyHomePage />;
};

const authLog = loggers.auth;

// Auth0 Callback Component
// Note: The actual redirect after login is handled by onRedirectCallback in Auth0Provider
// This component just shows a loading state while Auth0 processes the callback
const Auth0Callback = () => {
  const { isAuthenticated, isLoading, error, user } = useAuth0();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isLoading) {
      authLog.debug('Callback processed, isAuthenticated:', isAuthenticated);
      // If authenticated and not loading, navigate to returnTo (or home fallback)
      // This handles cases where onRedirectCallback doesn't fire (e.g., cached tokens)
      if (isAuthenticated) {
        const returnTo = consumeAuthReturnTo();
        authLog.debug('Navigating after auth callback:', returnTo || '/');
        navigate(returnTo || '/');
      }
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  // Redirect Auth0 errors to the proper error page
  React.useEffect(() => {
    if (error) {
      authLog.error('Callback error:', error);
      const errorMsg = encodeURIComponent(error.message || 'Authentication failed');
      // Determine the error reason based on Auth0 error
      let reason = 'oauth_error';
      if (error.error === 'access_denied') {
        reason = 'not_authorized';
      } else if (error.message?.toLowerCase().includes('email') && error.message?.toLowerCase().includes('verif')) {
        reason = 'email_not_verified';
      }
      navigate(`/auth-error?reason=${reason}&message=${errorMsg}`);
    }
  }, [error, navigate]);
  
  return (
    <div className="min-h-screen bg-gaia-bg flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gaia-primary"></div>
    </div>
  );
};

// Login Page Component
const LoginPage = () => {
  const { login, authVerificationPending } = useAuth();
  const returnTo = React.useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const param = searchParams.get('returnTo');
    const normalized = param && param.startsWith('/') && param !== '/callback' ? param : null;
    const stored = getAuthReturnTo();
    return normalized || stored;
  }, []);
  
  return (
    <div className="min-h-screen bg-gaia-bg flex items-center justify-center">
      <div className="bg-gaia-surface border border-gaia-border rounded-lg p-8 max-w-md w-full">
        <h1 className="text-3xl font-bold text-white mb-2">Welcome to Gaia</h1>
        <p className="text-gaia-muted mb-6">Sign in to continue your adventure</p>
        <button
          disabled={authVerificationPending}
          onClick={() => {
            if (returnTo) {
              setAuthReturnTo(returnTo);
              login({ appState: { returnTo } });
              return;
            }
            login();
          }}
          className={`${getButtonClass('primary')} disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          Sign in with Auth0
        </button>
        {authVerificationPending && (
          <p className="mt-4 text-sm text-gaia-muted">
            Session detected. Waiting for the backend to finish starting up...
          </p>
        )}
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, login, getAccessTokenSilently, authVerificationPending, sessionRevalidating } = useAuth();
  const location = window.location;
  const [registrationStatus, setRegistrationStatus] = React.useState('checking'); // checking, pending, completed
  const [showRegistration, setShowRegistration] = React.useState(false);

  React.useEffect(() => {
    // If not authenticated and not loading, trigger login with returnTo
    if (!loading && !isAuthenticated) {
      authLog.debug('Not authenticated, triggering login');
      const returnTo = location.pathname + location.search;
      setAuthReturnTo(returnTo);
      login({ appState: { returnTo } });
    }
  }, [isAuthenticated, loading, login, location]);

  // Check registration status after authentication
  React.useEffect(() => {
    if (isAuthenticated && user) {
      authLog.debug('Authenticated, checking registration status');
      checkRegistrationStatus();
    }
    // Existing registration callback intentionally owns its request lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const checkRegistrationStatus = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(apiUrl('/api/auth/registration-status'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const status = await response.json();
        authLog.debug('Registration status:', status.registration_status);
        // Show registration flow if user hasn't completed registration OR if they're awaiting approval
        if (status.registration_status === 'pending' || !status.is_authorized) {
          setRegistrationStatus('pending');
          setShowRegistration(true);
        } else {
          setRegistrationStatus('completed');
          setShowRegistration(false);
        }
      } else if (response.status === 403) {
        // User has pending registration or is awaiting approval
        setRegistrationStatus('pending');
        setShowRegistration(true);
      } else {
        // Assume completed if we can't check
        authLog.warn('Failed to check registration status, assuming completed');
        setRegistrationStatus('completed');
      }
    } catch (error) {
      authLog.error('Error checking registration status:', error);
      // Assume completed if we can't check
      setRegistrationStatus('completed');
    }
  };

  const handleRegistrationComplete = () => {
    setShowRegistration(false);
    setRegistrationStatus('completed');
  };

  if (loading || registrationStatus === 'checking') {
    return (
      <div className="min-h-screen bg-gaia-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gaia-primary"></div>
          {sessionRevalidating ? (
            <p className="text-sm text-gaia-muted text-center max-w-md px-4">
              Reconnecting to your session while the backend comes online...
            </p>
          ) : authVerificationPending && (
            <p className="text-sm text-gaia-muted text-center max-w-md px-4">
              Signing you in. The backend may still be waking up.
            </p>
          )}
        </div>
      </div>
    );
  }

  // If still not authenticated after effect, show loading
  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gaia-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gaia-primary"></div>
          {authVerificationPending && (
            <p className="text-sm text-gaia-muted text-center max-w-md px-4">
              Waiting for backend readiness before entering the app...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Show registration flow if needed
  if (showRegistration) {
    return <RegistrationFlow onComplete={handleRegistrationComplete} />;
  }

  // Wrap all protected routes with IdleTimeoutWrapper by default
  // This ensures users don't leave sessions open indefinitely
  return <IdleTimeoutWrapper>{children}</IdleTimeoutWrapper>;
};

// Registration Gate - shows registration flow if authenticated user hasn't completed registration
// Unlike ProtectedRoute, this doesn't force login - it only checks registration if already authenticated
const RegistrationGate = ({ children }) => {
  const { isAuthenticated, loading, user, getAccessTokenSilently, authVerificationPending, sessionRevalidating } = useAuth();
  const [registrationStatus, setRegistrationStatus] = React.useState('checking'); // checking, pending, completed
  const [showRegistration, setShowRegistration] = React.useState(false);

  React.useEffect(() => {
    // If not authenticated, skip registration check
    if (!isAuthenticated || !user) {
      setRegistrationStatus('completed'); // Allow access for unauthenticated users
      return;
    }

    const checkRegistration = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(apiUrl('/api/auth/registration-status'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const status = await response.json();
          if (status.registration_status === 'pending' || !status.is_authorized) {
            setRegistrationStatus('pending');
            setShowRegistration(true);
          } else {
            setRegistrationStatus('completed');
            setShowRegistration(false);
          }
        } else if (response.status === 403) {
          setRegistrationStatus('pending');
          setShowRegistration(true);
        } else {
          setRegistrationStatus('completed');
        }
      } catch (error) {
        authLog.error('Error checking registration status:', error);
        setRegistrationStatus('completed');
      }
    };

    checkRegistration();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  const handleRegistrationComplete = () => {
    setShowRegistration(false);
    setRegistrationStatus('completed');
  };

  if (loading || (isAuthenticated && registrationStatus === 'checking')) {
    return (
      <div className="min-h-screen bg-gaia-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gaia-primary"></div>
          {sessionRevalidating ? (
            <p className="text-sm text-gaia-muted text-center max-w-md px-4">
              Reconnecting to your session while the backend comes online...
            </p>
          ) : authVerificationPending && (
            <p className="text-sm text-gaia-muted text-center max-w-md px-4">
              Confirming your account while the backend comes online...
            </p>
          )}
        </div>
      </div>
    );
  }

  if (showRegistration) {
    return <RegistrationFlow onComplete={handleRegistrationComplete} />;
  }

  return children;
};

// Subscription Gate - requires active subscription for Adventure Mode access
const SubscriptionGate = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [hasSubscription, setHasSubscription] = React.useState(false);

  React.useEffect(() => {
    const checkSubscription = async () => {
      // Admins always have access
      if (user?.is_admin) {
        setHasSubscription(true);
        setLoading(false);
        return;
      }

      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(apiUrl('/api/billing/subscription'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          // Check for actual Stripe subscription with active or trialing status
          const hasActiveSubscription = data.has_subscription &&
            data.subscription_status &&
            ['active', 'trialing'].includes(data.subscription_status);
          setHasSubscription(hasActiveSubscription);

          if (!hasActiveSubscription) {
            // Redirect to Adventure Mode landing page to subscribe
            navigate('/adventure');
          }
        }
      } catch (err) {
        console.error('Error checking subscription:', err);
      } finally {
        setLoading(false);
      }
    };

    checkSubscription();
  }, [isAuthenticated, getAccessTokenSilently, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gaia-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gaia-primary"></div>
      </div>
    );
  }

  if (!hasSubscription) {
    // Will redirect via useEffect, show loading state
    return (
      <div className="min-h-screen bg-gaia-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">Subscription required for Adventure Mode</p>
          <button
            onClick={() => navigate('/adventure')}
            className={getButtonClass('primary')}
          >
            Subscribe to Adventure Mode
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// User Menu Component
export const UserMenu = () => {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = React.useState(false);

  if (!user) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-1 bg-gaia-surface border border-gaia-border rounded px-2 py-1 hover:bg-gaia-border transition-colors"
      >
        {user.picture_url ? (
          <img
            src={user.picture_url}
            alt={user.username}
            className="w-5 h-5 rounded-full"
          />
        ) : (
          <div className="w-5 h-5 rounded-full bg-gaia-primary flex items-center justify-center text-white text-xs">
            {(user.username || user.email)[0].toUpperCase()}
          </div>
        )}
        <span className="text-xs text-white">
          {user.username || user.email}
        </span>
        <svg className="w-3 h-3 text-gaia-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showMenu && (
        <div className="absolute right-0 mt-2 w-48 bg-gaia-surface border border-gaia-border rounded-lg shadow-lg overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-gaia-border">
            <p className="text-xs text-gaia-muted">Signed in as</p>
            <p className="text-sm text-white font-medium truncate">{user.email}</p>
            {user.is_admin && (
              <span className="inline-block mt-1 px-2 py-0.5 bg-gaia-primary text-xs text-white rounded">
                Admin
              </span>
            )}
          </div>
          <Link
            to="/subscription"
            onClick={() => setShowMenu(false)}
            className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-gaia-border transition-colors"
          >
            Subscription
          </Link>
          <Link
            to="/account/delete"
            onClick={() => setShowMenu(false)}
            className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gaia-border transition-colors"
          >
            Delete Account
          </Link>
          <button
            onClick={() => {
              setShowMenu(false);
              logout();
            }}
            className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gaia-border transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

// Main App with Auth0
const AppWithAuth0 = () => {
  // Check if we're in production mode
  const isProduction = window.location.hostname !== 'localhost' &&
                       window.location.hostname !== '127.0.0.1' &&
                       !window.location.hostname.startsWith('192.168.');

  // Check if Auth0 is configured — native apps fetch config at runtime via CapacitorHttp
  const auth0Configured = isNative() || (import.meta.env.VITE_AUTH0_DOMAIN && import.meta.env.VITE_AUTH0_CLIENT_ID);

  // Use Auth0 if configured OR explicitly required.
  const requireAuth = isProduction || import.meta.env.VITE_REQUIRE_AUTH === 'true' || auth0Configured;

  // Log the authentication mode (only once on mount, at info level)
  React.useEffect(() => {
    authLog.info(`Authentication mode: ${requireAuth && auth0Configured ? 'ENABLED' : 'DISABLED (Dev/Local)'}`);
    // Authentication mode is logged once per application mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If Auth0 is not configured, render with the development auth provider.
  if (!auth0Configured) {
    if (requireAuth) {
      authLog.warn('Authentication required but Auth0 not configured. Using DevAuthProvider.');
    }
    return (
      <Router>
        <MusicProvider>
        <DevAuthProvider>
          <MusicE2EGateBridge />
          <MusicRouteGateBridge />
          <AudioNotificationHost />
          <React.Suspense fallback={
            <div className="flex items-center justify-center min-h-screen bg-gray-900">
              <div className="text-gray-400 text-sm">Loading...</div>
            </div>
          }>
          <PlayerPreferencesProvider>
          <Routes>
            {/* Landing page — native app redirects to tavern */}
            <Route path="/" element={isNative() ? <Navigate to="/tavern-app" replace /> : <WelcomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/journey" element={<ProtectedRoute><JourneyGate /></ProtectedRoute>} />
            <Route path="/journey/character" element={<ProtectedRoute><JourneyCharacterPage /></ProtectedRoute>} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/billing/success" element={<RegistrationGate><BillingSuccessPage /></RegistrationGate>} />
            <Route path="/billing/cancel" element={<RegistrationGate><BillingCancelPage /></RegistrationGate>} />
            <Route path="/subscription" element={<RegistrationGate><SubscriptionPage /></RegistrationGate>} />
            <Route path="/account/delete" element={<RegistrationGate><DeleteAccountPage /></RegistrationGate>} />
            <Route path="/join" element={<ProtectedRoute><JoinCampaignPage /></ProtectedRoute>} />
            <Route path="/game-client-launch" element={<Navigate to="/" replace />} />
            <Route path="/:sessionId/dm" element={<App />} />
            <Route path="/:sessionId/player" element={<PlayerPage betaUi={true} />} />
            {/* Compatibility alias for existing beta links while the canonical
                /:sessionId/player route uses the beta player surface. */}
            <Route path="/:sessionId/player_beta" element={<PlayerPage betaUi={true} />} />
            <Route path="/:sessionId/combat" element={<PlayerPage />} />
            <Route path="/characters/fast-create" element={
              <ProtectedRoute>
                <React.Suspense fallback={
                  <div className="flex items-center justify-center min-h-screen bg-gray-900">
                    <div className="text-gray-400 text-sm">Loading…</div>
                  </div>
                }>
                  <FastCharacterCreator />
                </React.Suspense>
              </ProtectedRoute>
            } />
            <Route path="/characters/create" element={<CharacterCreationPage />} />
            <Route path="/characters/:characterId" element={<CharactersPage />} />
            <Route path="/characters" element={<CharactersPage />} />

            <Route path="/play-now" element={<NewCharacterFlowPage />} />
            {/* Same interview, different exit: saves the character and returns
                to the list instead of launching a Red Road campaign. */}
            <Route path="/characters/forge" element={<NewCharacterFlowPage mode="forge" />} />

            {/* Companion route - NOT under /admin; see CompanionFlowPage.jsx for the
                admin-only-in-this-phase scoping note */}
            <Route path="/companion" element={<IdleTimeoutWrapper><CompanionFlowPage /></IdleTimeoutWrapper>} />

            {/* Tavern routes - one-on-one character conversations */}
            <Route path="/tavern" element={<TavernPage />} />
            <Route path="/tavern/:characterId" element={<TavernChatPage />} />

            {/* Adventure Mode landing page */}
            <Route path="/adventure" element={<AdventureModeLanding />} />

            {/* Mobile tavern app routes */}
            <Route path="/tavern-app" element={<TavernAppShell />}>
              <Route index element={<TavernAppChat />} />
            </Route>

            {/* Admin routes - wrapped with IdleTimeoutWrapper to enforce session timeout */}
            <Route path="/admin" element={<IdleTimeoutWrapper><AdminIndex /></IdleTimeoutWrapper>} />
            <Route path="/admin/conversation-designer" element={<IdleTimeoutWrapper><AdminRoute allowDevBypass><ConversationDesignerPage /></AdminRoute></IdleTimeoutWrapper>} />
            <Route path="/admin/prompts" element={<IdleTimeoutWrapper><AgentSettings /></IdleTimeoutWrapper>} />
            <Route path="/admin/scenes" element={<IdleTimeoutWrapper><SceneInspector /></IdleTimeoutWrapper>} />
            <Route path="/admin/users" element={<IdleTimeoutWrapper><UserManagement /></IdleTimeoutWrapper>} />
            <Route path="/admin/campaigns" element={<IdleTimeoutWrapper><CampaignInspector /></IdleTimeoutWrapper>} />
            <Route path="/admin/characters" element={<IdleTimeoutWrapper><CharacterInspector /></IdleTimeoutWrapper>} />
            <Route path="/admin/startup-test" element={<IdleTimeoutWrapper><CampaignStartupTester /></IdleTimeoutWrapper>} />
            <Route path="/admin/preferences" element={<IdleTimeoutWrapper><PreferencesInspector /></IdleTimeoutWrapper>} />
            <Route path="/admin/agent-settings" element={<IdleTimeoutWrapper><AgentSettings /></IdleTimeoutWrapper>} />
            <Route path="/admin/event-timeline" element={<IdleTimeoutWrapper><StructuredTimeline /></IdleTimeoutWrapper>} />
            <Route path="/admin/campaign-viewer" element={<IdleTimeoutWrapper><CampaignViewer /></IdleTimeoutWrapper>} />
            <Route path="/admin/scene-flows" element={<IdleTimeoutWrapper><SceneFlowInspector /></IdleTimeoutWrapper>} />
            <Route path="/admin/npc-creator" element={<IdleTimeoutWrapper><NPCCreator /></IdleTimeoutWrapper>} />
            <Route path="/admin/scene-images" element={<IdleTimeoutWrapper><SceneImageVisualization /></IdleTimeoutWrapper>} />
            <Route path="/admin/pregeneration" element={<IdleTimeoutWrapper><PregenerationAdminRouteLayout /></IdleTimeoutWrapper>}>
              <Route path=":pregenId/pipeline" element={<PregenerationPipelineRoute />} />
            </Route>
            {/* Pregeneration editing workbench (BOU-2130): campaign entry + goal deep-link.
                Mirrors the /admin/pregeneration guard/wrap in this tree (IdleTimeoutWrapper). */}
            <Route path="/admin/pregeneration/:pregenId/workbench" element={<IdleTimeoutWrapper><PregenerationWorkbenchPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/pregeneration/:pregenId/workbench/:contentType/:contentKey" element={<IdleTimeoutWrapper><PregenerationWorkbenchPage /></IdleTimeoutWrapper>} />
            {/* The long-running tools below are wrapped like every other route. They
                used to be mounted bare so the 10-minute redirect could not discard an
                in-flight generation — but bare meant NO guard at all here, and a tab
                left on one kept the backend instance warm indefinitely (BOU-2974).
                Each of these components now asks the wrapper for the hour-long tier
                itself (useLongRunningIdleTimeout), which is also how they behave under
                the production tree's single ProtectedRoute wrapper — so the tier lives
                in one place and dev matches prod. */}
            <Route path="/admin/image-lab" element={<IdleTimeoutWrapper><ImageLabRoute /></IdleTimeoutWrapper>} />
            {/* BOU-2792: direct deep links into Image Lab's nested tabs, mirroring the
                /admin/image-lab route above. */}
            <Route path="/admin/image/cards" element={<IdleTimeoutWrapper><ImageLabRoute surface="cards" /></IdleTimeoutWrapper>} />
            <Route path="/admin/image/weapon-cards" element={<IdleTimeoutWrapper><ImageLabRoute surface="weapon-cards" /></IdleTimeoutWrapper>} />
            <Route path="/admin/image/weapon-art" element={<IdleTimeoutWrapper><ImageLabRoute surface="weapon-art" /></IdleTimeoutWrapper>} />
            <Route path="/admin/image/soul-forge" element={<IdleTimeoutWrapper><ImageLabRoute surface="soul-forge" /></IdleTimeoutWrapper>} />
            <Route path="/admin/scene-flight" element={<IdleTimeoutWrapper><SceneFlightLab /></IdleTimeoutWrapper>} />
            <Route path="/admin/journey-lab" element={<IdleTimeoutWrapper><JourneyLab /></IdleTimeoutWrapper>} />
            <Route path="/admin/journey-library" element={<IdleTimeoutWrapper><JourneyLibrary /></IdleTimeoutWrapper>} />
            <Route path="/admin/journey-travellers" element={<IdleTimeoutWrapper><JourneyTravellers /></IdleTimeoutWrapper>} />
            <Route path="/admin/journey-equipment" element={<IdleTimeoutWrapper><JourneyEquipment /></IdleTimeoutWrapper>} />
            <Route path="/admin/journey-npcs" element={<IdleTimeoutWrapper><JourneyNpcs /></IdleTimeoutWrapper>} />
            <Route path="/admin/journey-pois" element={<IdleTimeoutWrapper><JourneyPois /></IdleTimeoutWrapper>} />
            <Route path="/admin/journey-borders" element={<IdleTimeoutWrapper><JourneyBorders /></IdleTimeoutWrapper>} />
            <Route path="/admin/journey-texture-lab" element={<IdleTimeoutWrapper><JourneyTextureLab /></IdleTimeoutWrapper>} />
            <Route path="/admin/video" element={<IdleTimeoutWrapper><VideoLab /></IdleTimeoutWrapper>} />
            <Route path="/admin/activity" element={<IdleTimeoutWrapper><ActivityDashboard /></IdleTimeoutWrapper>} />
            <Route path="/admin/knowledge" element={<IdleTimeoutWrapper><KnowledgeLayerViewer /></IdleTimeoutWrapper>} />
            <Route path="/admin/soul-forge" element={<IdleTimeoutWrapper><SoulForgeAdmin /></IdleTimeoutWrapper>} />
            <Route path="/admin/voices" element={<IdleTimeoutWrapper><VoiceManager /></IdleTimeoutWrapper>} />
            <Route path="/admin/turn-integrity" element={<IdleTimeoutWrapper><TurnIntegrityDashboard /></IdleTimeoutWrapper>} />
            <Route path="/admin/interactive-scene" element={<IdleTimeoutWrapper><InteractiveSceneLab /></IdleTimeoutWrapper>} />
            <Route path="/admin/character-placement" element={<IdleTimeoutWrapper><CharacterPlacementLab /></IdleTimeoutWrapper>} />
            <Route path="/admin/three-lab" element={<AdminRoute allowDevBypass><IdleTimeoutWrapper><ThreeLab /></IdleTimeoutWrapper></AdminRoute>} />
            <Route path="/admin/companion-studio" element={<IdleTimeoutWrapper><CompanionStudioPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/narrative-arcs/*" element={<AdminRoute allowDevBypass><IdleTimeoutWrapper><NarrativeArcRoutePage /></IdleTimeoutWrapper></AdminRoute>} />
            {/* Combat Playground: setup keeps the default window; the /combat route
                takes the hour-long tier from inside the component, so a long fight is
                never redirected mid-turn and an abandoned one still lets go. */}
            <Route path="/admin/combat-playground" element={<IdleTimeoutWrapper><CombatPlaygroundPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/weapon-mechanics-lab" element={<IdleTimeoutWrapper><WeaponMechanicsLabPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/weapon-flow-lab" element={<IdleTimeoutWrapper><WeaponFlowLabPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/combat-playground/:combatId/combat" element={<IdleTimeoutWrapper><CombatPlaygroundPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/combat-animation-lab" element={<IdleTimeoutWrapper><CombatAnimationLabPage /></IdleTimeoutWrapper>} />
            {/* Wrapped like every sibling: dressing a roster is a long sit,
                and there is no reason this page should outlive the idle window
                that governs the labs beside it. */}
            <Route path="/admin/animation-editor" element={<IdleTimeoutWrapper><AnimationEditorPage /></IdleTimeoutWrapper>} />

            {/* Combat Arena (player): mirrors the Playground's idle treatment —
                setup on the default window, the fight on the hour-long tier. */}
            <Route path="/arena" element={<IdleTimeoutWrapper><ArenaPage /></IdleTimeoutWrapper>} />
            <Route path="/arena/:combatId" element={<IdleTimeoutWrapper><ArenaPage /></IdleTimeoutWrapper>} />

            {/* Debug routes - wrapped with IdleTimeoutWrapper to enforce session timeout */}
            <Route path="/admin/debug-audio" element={
              <IdleTimeoutWrapper>
                <AudioStreamProvider>
                  <AudioDebugPage />
                </AudioStreamProvider>
              </IdleTimeoutWrapper>
            } />
            <Route path="/admin/debug-images" element={
              <IdleTimeoutWrapper>
                <ImageDebugPage />
              </IdleTimeoutWrapper>
            } />
            <Route path="/test/collaborative-editor" element={<IdleTimeoutWrapper><CollaborativeEditorTest /></IdleTimeoutWrapper>} />
            <Route path="/admin/debug-scene-images" element={<IdleTimeoutWrapper><SceneImagesDebugPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/scene-creator-test" element={<IdleTimeoutWrapper><SceneCreatorTestPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/scene-transition-test" element={<IdleTimeoutWrapper><SceneTransitionTestPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/campaign-generation-test" element={<IdleTimeoutWrapper><CampaignGenerationTestPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/character-extraction-test" element={<IdleTimeoutWrapper><CharacterExtractionTestPage /></IdleTimeoutWrapper>} />
            <Route path="/admin/context-test" element={<IdleTimeoutWrapper><ContextTestPage /></IdleTimeoutWrapper>} />
            <Route path="/:campaignId/test/dice-roller" element={<IdleTimeoutWrapper><DiceRollerTest /></IdleTimeoutWrapper>} />
            <Route path="/test/dice-roller" element={<IdleTimeoutWrapper><DiceRollerTest /></IdleTimeoutWrapper>} />
            <Route path="/test/combat-display" element={<IdleTimeoutWrapper><CombatDisplayTestPage /></IdleTimeoutWrapper>} />

            {/* Legacy route redirects */}
            <Route path="/player" element={<Navigate to="/" replace />} />
            <Route path="/auth-error" element={<AuthError />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </PlayerPreferencesProvider>
          </React.Suspense>
        </DevAuthProvider>
        </MusicProvider>
      </Router>
    );
  }

  // Production mode with Auth0 - wrap everything in Auth0Provider
  return (
    <Router>
      <MusicProvider>
      <Auth0Provider>
        <Auth0AuthProvider>
          <MusicE2EGateBridge />
          <MusicRouteGateBridge />
          <AudioNotificationHost />
          <React.Suspense fallback={
            <div className="flex items-center justify-center min-h-screen bg-gray-900">
              <div className="text-gray-400 text-sm">Loading...</div>
            </div>
          }>
          <PlayerPreferencesProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/callback" element={<Auth0Callback />} />
            <Route path="/auth-error" element={<AuthError />} />
            <Route path="/billing/success" element={<RegistrationGate><BillingSuccessPage /></RegistrationGate>} />
            <Route path="/billing/cancel" element={<RegistrationGate><BillingCancelPage /></RegistrationGate>} />
            <Route path="/subscription" element={<RegistrationGate><SubscriptionPage /></RegistrationGate>} />
            <Route path="/" element={isNative() ? <Navigate to="/tavern-app" replace /> : <RegistrationGate><WelcomePage /></RegistrationGate>} />
            <Route path="/join" element={<ProtectedRoute><JoinCampaignPage /></ProtectedRoute>} />
            <Route path="/game-client-launch" element={<Navigate to="/" replace />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/journey" element={<ProtectedRoute><JourneyGate /></ProtectedRoute>} />
            <Route path="/journey/character" element={<ProtectedRoute><JourneyCharacterPage /></ProtectedRoute>} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/adventure" element={<RegistrationGate><AdventureModeLanding /></RegistrationGate>} />
            <Route
              element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              {/* Debug routes - IdleTimeoutWrapper is applied by ProtectedRoute */}
              <Route path="/admin/debug-audio" element={<AudioStreamProvider><AudioDebugPage /></AudioStreamProvider>} />
              <Route path="/admin/debug-images" element={<ImageDebugPage />} />
              <Route path="/test/collaborative-editor" element={<CollaborativeEditorTest />} />
              <Route path="/admin/debug-scene-images" element={<SceneImagesDebugPage />} />
              <Route path="/admin/scene-creator-test" element={<SceneCreatorTestPage />} />
              <Route path="/admin/scene-transition-test" element={<SceneTransitionTestPage />} />
              <Route path="/admin/campaign-generation-test" element={<CampaignGenerationTestPage />} />
              <Route path="/admin/character-extraction-test" element={<CharacterExtractionTestPage />} />
              <Route path="/admin/context-test" element={<ContextTestPage />} />
              <Route path="/:campaignId/test/dice-roller" element={<DiceRollerTest />} />
              <Route path="/test/dice-roller" element={<DiceRollerTest />} />
              <Route path="/test/combat-display" element={<CombatDisplayTestPage />} />

              {/* Protected routes */}
              <Route
                path="/:sessionId/dm"
                element={<App />}
              />
              <Route
                path="/:sessionId/player"
                element={<PlayerPage betaUi={true} />}
              />
              {/* Compatibility alias for existing beta links, mirrored from above. */}
              <Route
                path="/:sessionId/player_beta"
                element={<PlayerPage betaUi={true} />}
              />
              <Route
                path="/:sessionId/combat"
                element={<PlayerPage />}
              />
              <Route
                path="/characters/fast-create"
                element={
                  <ProtectedRoute>
                    <React.Suspense fallback={
                      <div className="flex items-center justify-center min-h-screen bg-gray-900">
                        <div className="text-gray-400 text-sm">Loading…</div>
                      </div>
                    }>
                      <FastCharacterCreator />
                    </React.Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/characters/create"
                element={<CharacterCreationPage />}
              />
              <Route
                path="/characters/:characterId"
                element={<CharactersPage />}
              />
              <Route
                path="/characters"
                element={<CharactersPage />}
              />

              <Route path="/play-now" element={<NewCharacterFlowPage />} />
              {/* Same interview, different exit: saves the character and returns
                  to the list instead of launching a Red Road campaign. */}
              <Route path="/characters/forge" element={<NewCharacterFlowPage mode="forge" />} />

              {/* Companion route - NOT under /admin; see CompanionFlowPage.jsx for the
                  admin-only-in-this-phase scoping note. IdleTimeoutWrapper is applied by
                  ProtectedRoute here, same as the other routes in this Outlet group. */}
              <Route path="/companion" element={<CompanionFlowPage />} />

              {/* Tavern routes - one-on-one character conversations */}
              <Route
                path="/tavern"
                element={<TavernPage />}
              />
              <Route
                path="/tavern/:characterId"
                element={<TavernChatPage />}
              />

              {/* Account management */}
              <Route path="/account/delete" element={<DeleteAccountPage />} />

              {/* Mobile tavern app routes */}
              <Route path="/tavern-app" element={<TavernAppShell />}>
                <Route index element={<TavernAppChat />} />
              </Route>

              {/* Admin routes - IdleTimeoutWrapper is applied by ProtectedRoute */}
              <Route path="/admin" element={<AdminIndex />} />
              <Route path="/admin/conversation-designer" element={<AdminRoute allowDevBypass><ConversationDesignerPage /></AdminRoute>} />
              <Route path="/admin/prompts" element={<AgentSettings />} />
              <Route path="/admin/scenes" element={<SceneInspector />} />
              <Route path="/admin/users" element={<UserManagement />} />
              <Route path="/admin/campaigns" element={<CampaignInspector />} />
              <Route path="/admin/characters" element={<CharacterInspector />} />
              <Route path="/admin/startup-test" element={<CampaignStartupTester />} />
              <Route path="/admin/preferences" element={<PreferencesInspector />} />
              <Route path="/admin/agent-settings" element={<AgentSettings />} />
              <Route path="/admin/event-timeline" element={<StructuredTimeline />} />
              <Route path="/admin/campaign-viewer" element={<CampaignViewer />} />
              <Route path="/admin/scene-flows" element={<SceneFlowInspector />} />
              <Route path="/admin/npc-creator" element={<NPCCreator />} />
              <Route path="/admin/scene-images" element={<SceneImageVisualization />} />
              <Route path="/admin/pregeneration" element={<PregenerationAdminRouteLayout />}>
                <Route path=":pregenId/pipeline" element={<PregenerationPipelineRoute />} />
              </Route>
              {/* Pregeneration editing workbench (BOU-2130): campaign entry + goal deep-link.
                  Mirrors the /admin/pregeneration guard/wrap in this tree (bare; IdleTimeoutWrapper
                  is applied by ProtectedRoute for this Outlet group). */}
              <Route path="/admin/pregeneration/:pregenId/workbench" element={<PregenerationWorkbenchPage />} />
              <Route path="/admin/pregeneration/:pregenId/workbench/:contentType/:contentKey" element={<PregenerationWorkbenchPage />} />
              <Route path="/admin/image-lab" element={<ImageLabRoute />} />
              {/* BOU-2792: direct deep links into Image Lab's nested tabs, mirroring the
                  bare /admin/image-lab route above (IdleTimeoutWrapper is applied by
                  ProtectedRoute for this whole Outlet group). */}
              <Route path="/admin/image/cards" element={<ImageLabRoute surface="cards" />} />
              <Route path="/admin/image/weapon-cards" element={<ImageLabRoute surface="weapon-cards" />} />
              <Route path="/admin/image/weapon-art" element={<ImageLabRoute surface="weapon-art" />} />
              <Route path="/admin/image/soul-forge" element={<ImageLabRoute surface="soul-forge" />} />
            {/* The long-running tools below take the hour-long idle tier from inside
                the component (useLongRunningIdleTimeout), because this Outlet group
                applies the wrapper via ProtectedRoute — a per-route prop cannot reach
                it, and dropping the wrapper from the route alone does nothing. */}
            <Route path="/admin/scene-flight" element={<SceneFlightLab />} />
            <Route path="/admin/journey-lab" element={<JourneyLab />} />
            <Route path="/admin/journey-library" element={<JourneyLibrary />} />
            <Route path="/admin/journey-travellers" element={<JourneyTravellers />} />
            <Route path="/admin/journey-equipment" element={<JourneyEquipment />} />
            <Route path="/admin/journey-npcs" element={<JourneyNpcs />} />
            <Route path="/admin/journey-pois" element={<JourneyPois />} />
            <Route path="/admin/journey-borders" element={<JourneyBorders />} />
            <Route path="/admin/journey-texture-lab" element={<JourneyTextureLab />} />
            <Route path="/admin/video" element={<VideoLab />} />
              <Route path="/admin/activity" element={<ActivityDashboard />} />
              <Route path="/admin/knowledge" element={<KnowledgeLayerViewer />} />
              <Route path="/admin/soul-forge" element={<SoulForgeAdmin />} />
              <Route path="/admin/voices" element={<VoiceManager />} />
              <Route path="/admin/turn-integrity" element={<TurnIntegrityDashboard />} />
              <Route path="/admin/interactive-scene" element={<InteractiveSceneLab />} />
              <Route path="/admin/character-placement" element={<CharacterPlacementLab />} />
              <Route path="/admin/three-lab" element={<AdminRoute><ThreeLab /></AdminRoute>} />
              <Route path="/admin/companion-studio" element={<CompanionStudioPage />} />
              <Route path="/admin/narrative-arcs/*" element={<AdminRoute><NarrativeArcRoutePage /></AdminRoute>} />
              {/* Combat Playground (IdleTimeoutWrapper via ProtectedRoute; the
                  /combat route takes the hour-long tier from the component) */}
              <Route path="/admin/combat-playground" element={<CombatPlaygroundPage />} />
              <Route path="/admin/weapon-mechanics-lab" element={<WeaponMechanicsLabPage />} />
              <Route path="/admin/weapon-flow-lab" element={<WeaponFlowLabPage />} />
              <Route path="/admin/combat-playground/:combatId/combat" element={<CombatPlaygroundPage />} />
              <Route path="/admin/combat-animation-lab" element={<CombatAnimationLabPage />} />
              {/* Both trees, deliberately: this file mounts admin routes twice
                  (authed shell and the protected tree), and a route added to
                  only one 404s for half the entry paths. */}
              <Route path="/admin/animation-editor" element={<AnimationEditorPage />} />

              {/* Combat Arena (player auth via ProtectedRoute, same as the other
                  routes in this Outlet group) */}
              <Route path="/arena" element={<ArenaPage />} />
              <Route path="/arena/:combatId" element={<ArenaPage />} />

              {/* Legacy route redirects */}
              <Route path="/player" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </PlayerPreferencesProvider>
          </React.Suspense>
        </Auth0AuthProvider>
      </Auth0Provider>
      </MusicProvider>
    </Router>
  );
};

export default AppWithAuth0;
